// @ts-strict-ignore
import { useDispatch, useSelector } from 'react-redux';
import { getUpdated, getIsLoading, getError, getRolloutDaysToPercentageFromAutomation } from '../helpers';
import { RootState } from '../reducers/reducers';
import { AutomationAutoStagedRollout, HuaweiAppGallerySettings } from '../types';
import * as React from 'react';
import { AppSettingsUpdatable } from '../actions/updatable';
import { tryUpdateAppSettings, UpdateHuaweiAppStoreReleaseDefaults } from '../actions/appSettings';
import { AppComponentProps, Nullable } from '../typescriptHelpers';
import { selectAppStoreDefaults, selectAutomationById } from '../selectors/selectors';
import { globalMessage } from '../utils/globalMessage';
import EditHuaweiReleaseSettings from './appSettings/EditHuaweiReleaseSettings';

interface EditHuaweiAppReleaseDefaultsProps extends AppComponentProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    autoStagedRolloutAutomationId: Nullable<string>;
}

const EditHuaweiAppReleaseDefaults: React.FC<EditHuaweiAppReleaseDefaultsProps> = (
    props: EditHuaweiAppReleaseDefaultsProps,
) => {
    const dispatch = useDispatch();

    const appStoreDefaults = useSelector(
        (state: RootState) => selectAppStoreDefaults(state, props.appId) as HuaweiAppGallerySettings,
    );

    const autoStagedRolloutAutomation = useSelector((state: RootState) =>
        selectAutomationById(state, props.appId, props.autoStagedRolloutAutomationId),
    ) as AutomationAutoStagedRollout;

    const customStagedRolloutEnabled = autoStagedRolloutAutomation?.enabled;

    const customStagedRolloutDaysToPercent = getRolloutDaysToPercentageFromAutomation(autoStagedRolloutAutomation);

    const releaseSettingsUpdatedSuccess = useSelector((state: RootState) =>
        getUpdated(AppSettingsUpdatable.UpdateAppGalleryAppStoreReleaseDefaults, state),
    );

    const releaseSettingsUpdateLoading = useSelector((state: RootState) =>
        getIsLoading(AppSettingsUpdatable.UpdateAppGalleryAppStoreReleaseDefaults, state),
    );

    const releaseSettingsUpdateError = useSelector((state: RootState) =>
        getError(AppSettingsUpdatable.UpdateAppGalleryAppStoreReleaseDefaults, state),
    );

    const onSaveChanges = (stagedRelease: boolean, initialStagedRolloutPercentage?: number) => {
        const request: UpdateHuaweiAppStoreReleaseDefaults = {
            orgId: props.orgId,
            appId: props.appId,
            stagedRelease,
            initialStagedRolloutPercentage,
            rolloutPercentage: {
                fraction: initialStagedRolloutPercentage ? initialStagedRolloutPercentage / 100 : 0,
            },
        };

        dispatch(tryUpdateAppSettings(AppSettingsUpdatable.UpdateAppGalleryAppStoreReleaseDefaults, request));
    };

    React.useEffect(() => {
        if (releaseSettingsUpdatedSuccess) {
            globalMessage.success('AppGallery release defaults updated!');

            props.onSuccess();
        }
    }, [releaseSettingsUpdatedSuccess]);

    React.useEffect(() => {
        if (releaseSettingsUpdateError) {
            globalMessage.error(releaseSettingsUpdateError);
        }
    }, [releaseSettingsUpdateError]);

    return (
        <EditHuaweiReleaseSettings
            open={props.open}
            isConfiguringAppDefaults={true}
            isLoading={releaseSettingsUpdateLoading}
            customStagedRolloutEnabled={customStagedRolloutEnabled}
            customStagedRolloutDaysToPercent={customStagedRolloutDaysToPercent}
            huaweiAppGallerySettings={appStoreDefaults}
            onSubmit={onSaveChanges}
            onCancel={props.onClose}
        />
    );
};

export default EditHuaweiAppReleaseDefaults;
